import {
  Activity,
  Calendar,
  Car,
  Clock,
  DoorOpen,
  Droplet,
  ExternalLink,
  FileCheck,
  Fuel,
  Gauge,
  Leaf,
  MapPin,
  Shield,
  Users,
  Zap,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { calculateAvailabilityHours } from '@/utils/availability';
import { formatDate, formatDateTime, formatSnakeCase, isISODate } from '@/utils/format';

import type { CallWithContact } from '@repo/shared-types';

// Base URL for vehicle listings (same as backend redirect controller)
const VEHICLE_LISTING_BASE_URL = 'https://www.v12sportsandclassics.co.uk';

const normalizeVehicleUrl = (url: string): string => {
  if (url.startsWith('/')) {
    return VEHICLE_LISTING_BASE_URL + url;
  }
  return url;
};

interface OutcomeDataSectionProps {
  call: CallWithContact;
  isReviewed: boolean;
}

export const OutcomeDataSection = ({ call, isReviewed }: OutcomeDataSectionProps) => {
  // Tool mode (AVAILABILITY_SOURCE_TOOLS): show only what the designated tool
  // uploaded during the call — never the review agent's extraction. When the
  // tool didn't fire, tool_availability_data is null and the card stays hidden.
  const toolSourced = call.availability_source === 'tool';
  const effectiveAvailabilityData = toolSourced
    ? (call.tool_availability_data ?? undefined)
    : call.availability_data;
  const effectiveOutcome = call.outcome;
  const effectiveLeadData = call.lead_data;

  return (
    <>
      {/* Shift Data */}
      {call.shift_data &&
        (effectiveOutcome === 'SHIFT_CONFIRMED' || effectiveOutcome === 'SHIFT_DECLINED') && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">
                {effectiveOutcome === 'SHIFT_CONFIRMED' ? 'Shift Confirmed' : 'Shift Declined'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div
                className={`rounded-lg border p-3 ${
                  effectiveOutcome === 'SHIFT_CONFIRMED'
                    ? 'border-green-200 bg-green-50'
                    : 'border-red-200 bg-red-50'
                }`}
              >
                <div
                  className={`mb-2 flex items-center gap-2 font-medium ${
                    effectiveOutcome === 'SHIFT_CONFIRMED' ? 'text-green-700' : 'text-red-700'
                  }`}
                >
                  <span>{effectiveOutcome === 'SHIFT_CONFIRMED' ? '✅' : '❌'}</span>
                  <span>
                    {effectiveOutcome === 'SHIFT_CONFIRMED'
                      ? 'Shift attendance confirmed'
                      : 'Shift attendance declined'}
                  </span>
                </div>
                {call.shift_data.confirmed_at ? (
                  <p
                    className={`text-xs ${
                      effectiveOutcome === 'SHIFT_CONFIRMED' ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {effectiveOutcome === 'SHIFT_CONFIRMED' ? 'Confirmed' : 'Declined'} at:{' '}
                    {formatDateTime(String(call.shift_data.confirmed_at))}
                  </p>
                ) : null}
              </div>

              {call.shift_data.notes ? (
                <div>
                  <p className="text-muted-foreground text-sm font-medium">Notes</p>
                  <p className="mt-1 text-sm">{String(call.shift_data.notes)}</p>
                </div>
              ) : null}
            </CardContent>
          </Card>
        )}

      {/* USD Event */}
      {(effectiveOutcome === 'ATTENDING_USD_EVENT' ||
        effectiveOutcome === 'NOT_ATTENDING_USD_EVENT') && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {effectiveOutcome === 'ATTENDING_USD_EVENT'
                ? 'Attending USD Event'
                : 'Not Attending USD Event'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div
              className={`rounded-lg border p-3 ${
                effectiveOutcome === 'ATTENDING_USD_EVENT'
                  ? 'border-green-200 bg-green-50'
                  : 'border-red-200 bg-red-50'
              }`}
            >
              <div
                className={`mb-2 flex items-center gap-2 font-medium ${
                  effectiveOutcome === 'ATTENDING_USD_EVENT' ? 'text-green-700' : 'text-red-700'
                }`}
              >
                <span>{effectiveOutcome === 'ATTENDING_USD_EVENT' ? '✅' : '❌'}</span>
                <span>
                  {effectiveOutcome === 'ATTENDING_USD_EVENT'
                    ? 'Attending USD event'
                    : 'Not attending USD event'}
                </span>
              </div>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-muted-foreground text-xs tracking-widest uppercase">
                RBS Certificate
              </p>
              <span
                className={`mt-1 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                  (call as Record<string, unknown>).has_rbs_certificate
                    ? 'bg-emerald-100 text-emerald-900'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                {(call as Record<string, unknown>).has_rbs_certificate ? 'Yes' : 'No'}
              </span>
            </div>
            {effectiveAvailabilityData?.availability_slots &&
              Array.isArray(effectiveAvailabilityData.availability_slots) &&
              effectiveAvailabilityData.availability_slots.length > 0 && (
                <div className="rounded-lg border p-3">
                  <p className="text-muted-foreground mb-2 text-xs tracking-widest uppercase">
                    Available Shifts
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {effectiveAvailabilityData.availability_slots.map(
                      (slot: Record<string, unknown>, idx: number) => (
                        <span
                          key={idx}
                          className="bg-primary/10 text-primary inline-block rounded-full px-3 py-1 text-xs font-medium capitalize"
                        >
                          {slot.date
                            ? `${formatDate(slot.date as string)}${slot.shift_type ? ` - ${slot.shift_type}` : ''}`
                            : JSON.stringify(slot)}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      )}

      {/* Cook Availability */}
      {(effectiveOutcome === 'AVAILABLE_COOK' || effectiveOutcome === 'NOT_AVAILABLE_COOK') && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">
              {effectiveOutcome === 'AVAILABLE_COOK' ? 'Available Cook' : 'Not Available Cook'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div
              className={`rounded-lg border p-3 ${
                effectiveOutcome === 'AVAILABLE_COOK'
                  ? 'border-green-200 bg-green-50'
                  : 'border-red-200 bg-red-50'
              }`}
            >
              <div
                className={`mb-2 flex items-center gap-2 font-medium ${
                  effectiveOutcome === 'AVAILABLE_COOK' ? 'text-green-700' : 'text-red-700'
                }`}
              >
                <span>{effectiveOutcome === 'AVAILABLE_COOK' ? '✅' : '❌'}</span>
                <span>{effectiveOutcome === 'AVAILABLE_COOK' ? 'Available' : 'Not available'}</span>
              </div>
            </div>
            <div className="rounded-lg border p-3">
              <p className="text-muted-foreground text-xs tracking-widest uppercase">
                Valid Food Card
              </p>
              <span
                className={`mt-1 inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                  (call as Record<string, unknown>).has_valid_food_card
                    ? 'bg-emerald-100 text-emerald-900'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                {(call as Record<string, unknown>).has_valid_food_card ? 'Yes' : 'No'}
              </span>
            </div>
            {effectiveAvailabilityData?.availability_slots &&
              Array.isArray(effectiveAvailabilityData.availability_slots) &&
              effectiveAvailabilityData.availability_slots.length > 0 && (
                <div className="rounded-lg border p-3">
                  <p className="text-muted-foreground mb-2 text-xs tracking-widest uppercase">
                    Available Shifts
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {effectiveAvailabilityData.availability_slots.map(
                      (slot: Record<string, unknown>, idx: number) => (
                        <span
                          key={idx}
                          className="bg-primary/10 text-primary inline-block rounded-full px-3 py-1 text-xs font-medium capitalize"
                        >
                          {slot.date
                            ? `${formatDate(slot.date as string)}${slot.shift_type ? ` - ${slot.shift_type}` : ''}`
                            : JSON.stringify(slot)}
                        </span>
                      ),
                    )}
                  </div>
                </div>
              )}
          </CardContent>
        </Card>
      )}

      {/* Callback Data */}
      {call.callback_data && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Callback Requested</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
              <div className="mb-2 flex items-center gap-2 font-medium text-orange-700">
                <span>📞</span>
                <span>Customer requested callback from representative</span>
              </div>
              {call.callback_data.captured_at && (
                <p className="text-xs text-orange-600">
                  Requested at: {formatDateTime(call.callback_data.captured_at)}
                </p>
              )}
            </div>

            <div className="space-y-2">
              {call.callback_data.reason && (
                <div>
                  <p className="text-muted-foreground text-sm font-medium">Reason</p>
                  <p className="mt-1 text-sm">{call.callback_data.reason}</p>
                </div>
              )}
              {call.callback_data.customer_question && (
                <div>
                  <p className="text-muted-foreground text-sm font-medium">Customer Question</p>
                  <p className="mt-1 text-sm">{call.callback_data.customer_question}</p>
                </div>
              )}
              {call.callback_data.preferred_callback_time && (
                <div>
                  <p className="text-muted-foreground text-sm font-medium">
                    Preferred Callback Time
                  </p>
                  <p className="mt-1 text-sm">{call.callback_data.preferred_callback_time}</p>
                </div>
              )}
              {call.callback_data.additional_notes && (
                <div>
                  <p className="text-muted-foreground text-sm font-medium">Additional Notes</p>
                  <p className="mt-1 text-sm">{call.callback_data.additional_notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Availability Data */}
      {effectiveAvailabilityData &&
        Object.keys(effectiveAvailabilityData).length > 0 &&
        ![
          'ATTENDING_USD_EVENT',
          'NOT_ATTENDING_USD_EVENT',
          'AVAILABLE_COOK',
          'NOT_AVAILABLE_COOK',
        ].includes(effectiveOutcome) && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm">
                Availability data
                {toolSourced ? (
                  <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300">
                    Uploaded via {call.availability_source_tool}
                  </span>
                ) : (
                  isReviewed && (
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                      AI Verified
                    </span>
                  )
                )}
              </CardTitle>
              <CardDescription>
                {toolSourced
                  ? 'Slots uploaded directly by the agent tool during the call'
                  : isReviewed
                    ? 'Data verified by AI review'
                    : 'Structured slots captured by the agent'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {calculateAvailabilityHours(effectiveAvailabilityData as Record<string, unknown>) >
                0 && (
                <div className="flex items-center gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/30">
                  <Clock className="h-8 w-8 text-emerald-600 dark:text-emerald-400" />
                  <div>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                      {calculateAvailabilityHours(
                        effectiveAvailabilityData as Record<string, unknown>,
                      )}{' '}
                      hours
                    </p>
                    <p className="text-sm text-emerald-600 dark:text-emerald-400">
                      Total availability captured (
                      {effectiveAvailabilityData?.availability_slots &&
                      Array.isArray(effectiveAvailabilityData.availability_slots)
                        ? effectiveAvailabilityData.availability_slots.length
                        : 0}{' '}
                      shifts x 12 hours)
                    </p>
                  </div>
                </div>
              )}
              <div className="space-y-4">
                {Object.entries(effectiveAvailabilityData).map(([key, value]) => {
                  const formattedKey = formatSnakeCase(key);

                  return (
                    <div key={key} className="border-b pb-3 last:border-b-0 last:pb-0">
                      <p className="text-muted-foreground mb-2 text-xs tracking-widest uppercase">
                        {formattedKey}
                      </p>
                      <div className="mt-1">
                        {value === null || value === undefined ? (
                          <p className="text-muted-foreground text-sm italic">No additional info</p>
                        ) : Array.isArray(value) ? (
                          <div className="flex flex-wrap gap-2">
                            {value.length === 0 ? (
                              <p className="text-muted-foreground text-sm italic">None</p>
                            ) : (
                              value.map((item, idx) => {
                                const isDate = /^\d{4}-\d{2}-\d{2}$/.test(String(item));
                                const isObject = typeof item === 'object' && item !== null;

                                if (isObject) {
                                  return (
                                    <span
                                      key={idx}
                                      className="bg-primary/10 text-primary inline-block rounded-full px-3 py-1 text-xs font-medium capitalize"
                                    >
                                      {item.date
                                        ? `${formatDate(item.date as string)}${item.shift_type ? ` - ${item.shift_type}` : ''}`
                                        : JSON.stringify(item, null, 2)}
                                    </span>
                                  );
                                }

                                return (
                                  <span
                                    key={idx}
                                    className="bg-primary/10 text-primary rounded-full px-3 py-1 text-xs font-medium"
                                  >
                                    {isDate ? formatDate(item as string) : String(item)}
                                  </span>
                                );
                              })
                            )}
                          </div>
                        ) : typeof value === 'boolean' ? (
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                              value
                                ? 'bg-emerald-100 text-emerald-900'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            {value ? 'Yes' : 'No'}
                          </span>
                        ) : (
                          <p className="text-sm font-medium">{String(value)}</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

      {/* Lead Data */}
      {effectiveLeadData && Object.keys(effectiveLeadData).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm">
              Lead data
              {isReviewed && (
                <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                  AI Verified
                </span>
              )}
            </CardTitle>
            <CardDescription>
              {isReviewed ? 'Data verified by AI review' : 'Information captured about the lead'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {effectiveLeadData.customer_name && (
              <div className="mb-4 rounded-lg border-2 border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-950/30">
                <p className="text-muted-foreground mb-1 text-xs font-semibold tracking-wider uppercase">
                  Customer Name
                </p>
                <p className="text-xl font-bold text-blue-900 dark:text-blue-100">
                  {effectiveLeadData.customer_name}
                </p>
              </div>
            )}
            <div className="space-y-4">
              {Object.entries(effectiveLeadData)
                .filter(([key]) => key !== 'selected_vehicle' && key !== 'customer_name')
                .map(([key, value]) => {
                  const formattedKey = formatSnakeCase(key);

                  return (
                    <div key={key} className="border-b pb-3 last:border-b-0 last:pb-0">
                      <p className="text-muted-foreground mb-2 text-xs tracking-widest uppercase">
                        {formattedKey}
                      </p>
                      <div className="mt-1">
                        {value === null || value === undefined ? (
                          <p className="text-muted-foreground text-sm italic">No additional info</p>
                        ) : Array.isArray(value) ? (
                          <div className="flex flex-wrap gap-2">
                            {value.length === 0 ? (
                              <p className="text-muted-foreground text-sm italic">None</p>
                            ) : (
                              value.map((item, idx) => (
                                <span
                                  key={idx}
                                  className="bg-primary/10 text-primary rounded-full px-3 py-1 text-xs font-medium"
                                >
                                  {String(item)}
                                </span>
                              ))
                            )}
                          </div>
                        ) : typeof value === 'boolean' ? (
                          <span
                            className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
                              value
                                ? 'bg-emerald-100 text-emerald-900'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            {value ? 'Yes' : 'No'}
                          </span>
                        ) : isISODate(String(value)) ? (
                          <p className="text-sm font-medium">{formatDateTime(String(value))}</p>
                        ) : (
                          <p className="text-sm font-medium">
                            {String(value).includes('_')
                              ? formatSnakeCase(String(value))
                              : String(value)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected Vehicle */}
      {effectiveLeadData?.selected_vehicle && (
        <SelectedVehicleCard vehicle={effectiveLeadData.selected_vehicle as VehicleData} />
      )}
    </>
  );
};

type VehicleData = {
  id?: string;
  name?: string;
  make?: string;
  model?: string;
  year?: number | string;
  price?: number | string;
  miles?: number | string;
  mileage?: string;
  transmission?: string;
  exterior_color?: string;
  fuel_type?: string;
  body_type?: string;
  doors?: string | number;
  seating_capacity?: string | number;
  vehicle_registration_date?: string;
  num_owners?: string | number;
  vdp_url?: string;
  seller_street?: string;
  seller_city?: string;
  engine?: string;
  registration?: string;
  emission_class?: string;
  insurance_group?: string;
  trim?: string;
  co2_emissions?: string;
  fuel_efficiency?: string;
  power?: string;
  top_speed?: string;
  location?: string;
};

const formatPrice = (price: number | string) => {
  const numericPrice = typeof price === 'string' ? parseFloat(price.replace(/[^\d.]/g, '')) : price;
  if (isNaN(numericPrice)) return price;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(numericPrice);
};

const formatMiles = (miles: number | string) => {
  if (typeof miles === 'string') return miles;
  return `${new Intl.NumberFormat('en-GB').format(miles)} mi`;
};

const SelectedVehicleCard = ({ vehicle }: { vehicle: VehicleData }) => (
  <Card>
    <CardHeader className="pb-2">
      <div className="flex items-center gap-2">
        <Car className="text-muted-foreground h-5 w-5" />
        <CardTitle className="text-sm">Selected Vehicle</CardTitle>
      </div>
      <CardDescription>Vehicle the customer expressed interest in</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="space-y-6">
        {/* Vehicle Title & Price */}
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h3 className="text-xl font-semibold">
              {vehicle.name ||
                `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''}`.trim() ||
                'Vehicle'}
              {vehicle.trim && (
                <span className="text-muted-foreground ml-2 text-lg font-normal italic">
                  {vehicle.trim}
                </span>
              )}
            </h3>
            {vehicle.exterior_color && (
              <p className="text-muted-foreground mt-1 text-sm">
                {vehicle.exterior_color} &bull; {vehicle.body_type || 'N/A'}
              </p>
            )}
          </div>
          {vehicle.price && (
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {formatPrice(vehicle.price)}
            </div>
          )}
        </div>

        {/* Vehicle Specs Grid */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {(vehicle.miles !== undefined || vehicle.mileage) && (
            <div className="flex items-center gap-2">
              <Gauge className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Mileage</p>
                <p className="text-sm font-medium">
                  {formatMiles(vehicle.miles || vehicle.mileage || 0)}
                </p>
              </div>
            </div>
          )}
          {vehicle.fuel_type && (
            <div className="flex items-center gap-2">
              <Fuel className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Fuel</p>
                <p className="text-sm font-medium">{vehicle.fuel_type}</p>
              </div>
            </div>
          )}
          {vehicle.location && (
            <div className="flex items-center gap-2">
              <MapPin className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Location</p>
                <p className="text-sm font-medium">{vehicle.location}</p>
              </div>
            </div>
          )}
          {vehicle.transmission && (
            <div className="flex items-center gap-2">
              <Car className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Transmission</p>
                <p className="text-sm font-medium">{vehicle.transmission}</p>
              </div>
            </div>
          )}
          {vehicle.doors && (
            <div className="flex items-center gap-2">
              <DoorOpen className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Doors</p>
                <p className="text-sm font-medium">{vehicle.doors}</p>
              </div>
            </div>
          )}
          {vehicle.seating_capacity && (
            <div className="flex items-center gap-2">
              <Users className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Seats</p>
                <p className="text-sm font-medium">{vehicle.seating_capacity}</p>
              </div>
            </div>
          )}
          {vehicle.vehicle_registration_date && (
            <div className="flex items-center gap-2">
              <Calendar className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Registered</p>
                <p className="text-sm font-medium">
                  {new Date(vehicle.vehicle_registration_date).toLocaleDateString('en-GB', {
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>
          )}
          {vehicle.num_owners && (
            <div className="flex items-center gap-2">
              <Users className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Prev. Owners</p>
                <p className="text-sm font-medium">{vehicle.num_owners}</p>
              </div>
            </div>
          )}
          {vehicle.registration && (
            <div className="flex items-center gap-2">
              <FileCheck className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Registration</p>
                <p className="text-sm font-medium uppercase">{vehicle.registration}</p>
              </div>
            </div>
          )}
          {vehicle.engine && (
            <div className="flex items-center gap-2">
              <Zap className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Engine</p>
                <p className="text-sm font-medium">{vehicle.engine}</p>
              </div>
            </div>
          )}
          {vehicle.power && (
            <div className="flex items-center gap-2">
              <Activity className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Power</p>
                <p className="text-sm font-medium">{vehicle.power}</p>
              </div>
            </div>
          )}
          {vehicle.top_speed && (
            <div className="flex items-center gap-2">
              <Gauge className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Top Speed</p>
                <p className="text-sm font-medium">{vehicle.top_speed}</p>
              </div>
            </div>
          )}
          {vehicle.fuel_efficiency && (
            <div className="flex items-center gap-2">
              <Droplet className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Economy</p>
                <p className="text-sm font-medium">{vehicle.fuel_efficiency}</p>
              </div>
            </div>
          )}
          {vehicle.co2_emissions && (
            <div className="flex items-center gap-2">
              <Leaf className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">CO2 Emissions</p>
                <p className="text-sm font-medium">{vehicle.co2_emissions}</p>
              </div>
            </div>
          )}
          {vehicle.emission_class && (
            <div className="flex items-center gap-2">
              <Leaf className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Emission Class</p>
                <p className="text-sm font-medium">{vehicle.emission_class}</p>
              </div>
            </div>
          )}
          {vehicle.insurance_group && (
            <div className="flex items-center gap-2">
              <Shield className="text-muted-foreground h-4 w-4" />
              <div>
                <p className="text-muted-foreground text-xs">Insurance</p>
                <p className="text-sm font-medium">Group {vehicle.insurance_group}</p>
              </div>
            </div>
          )}
        </div>

        {/* Location */}
        {(vehicle.seller_street || vehicle.seller_city) && (
          <div className="text-muted-foreground flex items-center gap-2 text-sm">
            <MapPin className="h-4 w-4" />
            <span>{[vehicle.seller_street, vehicle.seller_city].filter(Boolean).join(', ')}</span>
          </div>
        )}

        {/* VDP Link */}
        {vehicle.vdp_url && (
          <Button asChild variant="default" className="w-full md:w-auto">
            <a
              href={normalizeVehicleUrl(vehicle.vdp_url)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              View Vehicle Listing
            </a>
          </Button>
        )}
      </div>
    </CardContent>
  </Card>
);
